// Version-neutral in-memory view of script.json.
//
// v3 and v4 differ in identity (v3 has no action IDs, per-episode scene IDs;
// v4 has global random IDs) but the *shape* an agent reads is identical. So we
// normalize once here and every tool works on one model. The `anchor` field is
// whatever string the agent can paste back for that node.
import * as fs from "node:fs";
import { step } from "./trace.js";
import { generateV4Id } from "./schema/index.js";
const KINDS = [
    ["actor", "actors", "actor"],
    ["location", "locations", "location"],
    ["prop", "props", "prop"],
];
function arr(v) { return Array.isArray(v) ? v : []; }
function str(v) { return typeof v === "string" ? v : ""; }
// Index by every alias that is globally unambiguous. Colliding keys are dropped
// rather than resolved first-wins — a silently wrong target is worse than an error.
function put(map, key, value, dupes) {
    if (!key)
        return;
    if (map.has(key) && map.get(key) !== value) {
        dupes.add(key);
        return;
    }
    map.set(key, value);
}
/** Read a script from disk. The returned document is bound: mutations write through. */
export function load(path) {
    return parse(JSON.parse(fs.readFileSync(path, "utf8")), path);
}
/**
 * Build a document from an in-memory object. Without a `path` the document is
 * *unbound*: mutations stay in memory and `save` refreshes indexes without disk
 * I/O. Call `commit` to choose when it hits disk.
 */
export function parse(raw, path = "") {
    const version = raw?.version;
    if (version !== 3 && version !== 4) {
        throw new Error(`Unsupported script.version: ${JSON.stringify(version)} (expected 3 or 4)`);
    }
    const doc = {
        version, path, raw,
        episodes: [], assets: [],
        byId: new Map(), eps: new Map(), scenes: new Map(), actions: new Map(),
    };
    for (const [kind, field, idKey] of KINDS) {
        for (const a of arr(raw[field])) {
            const asset = {
                id: str(a[`${idKey}_id`]), kind,
                name: str(a[`${idKey}_name`]),
                aliases: arr(a.aliases).map(str).filter(Boolean),
                importance: str(a.importance),
                desc: str(a.description),
                states: arr(a.states).map((s) => ({
                    id: str(s.state_id), name: str(s.state_name), desc: str(s.description),
                })),
                raw: a, label: "",
            };
            doc.assets.push(asset);
            doc.byId.set(asset.id, asset);
        }
    }
    // Screenwriters use short names. Pick the shortest label unique across the
    // whole cast; aliases stay resolvable on write, so this is lossless.
    const seen = new Map();
    for (const a of doc.assets)
        for (const n of [a.name, ...a.aliases])
            seen.set(n, (seen.get(n) ?? 0) + 1);
    for (const a of doc.assets) {
        a.label = [a.name, ...a.aliases]
            .filter((n) => seen.get(n) === 1)
            .sort((x, y) => x.length - y.length)[0] ?? a.name;
    }
    const dupes = new Set();
    let gScene = 0, gAction = 0;
    arr(raw.episodes).forEach((eRaw, ei) => {
        const ep = {
            anchor: String(ei + 1), id: str(eRaw.episode_id), no: ei + 1,
            title: str(eRaw.title) || undefined, synopsis: str(eRaw.synopsis) || undefined,
            scenes: [], raw: eRaw,
        };
        arr(eRaw.scenes).forEach((sRaw, si) => {
            gScene++;
            const scene = {
                anchor: version === 4 ? str(sRaw.scene_id) : `${ei + 1}-${si + 1}`,
                id: str(sRaw.scene_id), no: si + 1, gno: gScene,
                env: sRaw.environment ?? {},
                location: refOf(sRaw.location, "location_id"),
                actors: arr(sRaw.actors).map((r) => refOf(r, "actor_id")).filter(Boolean),
                props: arr(sRaw.props).map((r) => refOf(r, "prop_id")).filter(Boolean),
                actions: [], raw: sRaw, ep,
            };
            arr(sRaw.actions).forEach((aRaw, ai) => {
                gAction++;
                const action = {
                    anchor: version === 4 ? str(aRaw.action_id) : `${ei + 1}-${si + 1}-${ai + 1}`,
                    no: ai + 1, gno: gAction,
                    type: str(aRaw.type) || "action",
                    content: str(aRaw.content),
                    speaker: speakerOf(aRaw.speaker),
                    paren: typeof aRaw.parenthetical === "string" ? aRaw.parenthetical
                        : typeof aRaw.emotion === "string" ? aRaw.emotion : undefined,
                    ts: str(aRaw.extend?.timestamp) || undefined,
                    raw: aRaw, scene,
                };
                scene.actions.push(action);
                put(doc.actions, action.anchor, action, dupes);
                put(doc.actions, `${ei + 1}-${si + 1}-${ai + 1}`, action, dupes);
                if (version === 3)
                    put(doc.actions, `${ep.id}/${scene.id}#${ai}`, action, dupes);
            });
            ep.scenes.push(scene);
            put(doc.scenes, scene.anchor, scene, dupes);
            put(doc.scenes, `${ei + 1}-${si + 1}`, scene, dupes);
            put(doc.scenes, `${ep.id}/${scene.id}`, scene, dupes);
            put(doc.scenes, scene.id, scene, dupes);
        });
        doc.episodes.push(ep);
        put(doc.eps, String(ei + 1), ep, dupes);
        put(doc.eps, ep.id, ep, dupes);
    });
    for (const k of dupes) {
        doc.actions.delete(k);
        doc.scenes.delete(k);
        doc.eps.delete(k);
    }
    step(path ? "model.load" : "model.parse", `v${version} · ${doc.episodes.length} ep · ${doc.scenes.size} scene keys · `
        + `${doc.actions.size} action keys · ${doc.assets.length} assets · ${dupes.size} ambiguous keys dropped`);
    return doc;
}
function refOf(r, idKey) {
    if (!r || typeof r !== "object")
        return null;
    const id = str(r[idKey]);
    if (!id)
        return null;
    const states = Array.isArray(r.state_ids) ? r.state_ids.map(str)
        : r.state_id ? [str(r.state_id)] : [];
    return { id, states, raw: r };
}
function speakerOf(s) {
    if (!s || typeof s !== "object")
        return undefined;
    if (s.actor_id)
        return { id: str(s.actor_id), state: str(s.state_id) || undefined };
    if (s.kind)
        return { kind: str(s.kind), label: str(s.label) || undefined };
    return undefined;
}
/**
 * Resolve an address: ordinal (`3`, `3-2`, `3-2-5`), any canonical ID, an asset
 * name/alias, or a state as `<asset>/<state>` — where either half may be an id
 * or a name.
 */
export function resolve(doc, addr) {
    const q = addr.trim();
    if (!q)
        return { t: "script", doc };
    // `<asset>/<state>` before anything else: a v3 scene reads as `ep_001/scn_001`,
    // so only try the state reading when the left half is actually an asset.
    const slash = q.indexOf("/");
    if (slash > 0 && !doc.scenes.has(q)) {
        const left = q.slice(0, slash), right = q.slice(slash + 1);
        const owner = doc.byId.get(left) ?? assetByName(doc, left);
        if (owner) {
            const hit = owner.states.filter((st) => st.id === right || st.name === right);
            if (hit.length === 1) {
                step("resolve", `"${q}" -> state ${owner.id}/${hit[0].id} "${hit[0].name}"`);
                return { t: "state", asset: owner, state: hit[0] };
            }
            if (hit.length > 1) {
                throw new Error(`${owner.name} has ${hit.length} states named "${right}"; address it by state id`);
            }
            throw new Error(`${owner.name} has no state "${right}" `
                + `(has: ${owner.states.map((st) => `${st.id}(${st.name})`).join(", ")})`);
        }
    }
    const a = doc.actions.get(q);
    if (a) {
        step("resolve", `"${q}" -> action map hit -> ${a.anchor}`);
        return { t: "action", action: a };
    }
    const s = doc.scenes.get(q);
    if (s) {
        step("resolve", `"${q}" -> scene map hit -> ${s.anchor}`);
        return { t: "scene", scene: s };
    }
    const e = doc.eps.get(q);
    if (e) {
        step("resolve", `"${q}" -> episode map hit -> ${e.id}`);
        return { t: "ep", ep: e };
    }
    const byId = doc.byId.get(q);
    if (byId) {
        step("resolve", `"${q}" -> asset id hit -> ${byId.name}`);
        return { t: "asset", asset: byId };
    }
    const hits = doc.assets.filter((x) => x.name === q || x.aliases.includes(q));
    if (hits.length === 1) {
        step("resolve", `"${q}" -> exact name/alias -> ${hits[0].id} ${hits[0].name}`);
        return { t: "asset", asset: hits[0] };
    }
    if (hits.length > 1) {
        throw new Error(`Ambiguous name "${q}": ${hits.map((h) => `${h.id}(${h.name})`).join(", ")}`);
    }
    const loose = doc.assets.filter((x) => x.name.includes(q) || x.aliases.some((al) => al.includes(q)));
    if (loose.length === 1) {
        step("resolve", `"${q}" -> substring name match -> ${loose[0].id} ${loose[0].name}`);
        return { t: "asset", asset: loose[0] };
    }
    throw new Error(`Unknown address "${q}". Use an ordinal (3 / 3-2 / 3-2-5), a canonical ID, or an asset name.`
        + (loose.length ? ` Did you mean: ${loose.slice(0, 5).map((h) => h.name).join(", ")}?` : ""));
}
function assetByName(doc, q) {
    const exact = doc.assets.filter((x) => x.name === q || x.aliases.includes(q));
    if (exact.length === 1)
        return exact[0];
    if (exact.length > 1)
        return undefined;
    const loose = doc.assets.filter((x) => x.name.includes(q) || x.aliases.some((a) => a.includes(q)));
    return loose.length === 1 ? loose[0] : undefined;
}
export function assetName(doc, id) {
    return doc.byId.get(id)?.label || id;
}
export function stateName(doc, assetId, stateId) {
    const st = doc.byId.get(assetId)?.states.find((s) => s.id === stateId);
    return st?.name || stateId;
}
export function allActions(doc) {
    return doc.episodes.flatMap((e) => e.scenes.flatMap((s) => s.actions));
}
const KIND_OF = {
    ep: "episode", scn: "scene", ac: "action", act: "actor", loc: "location", prp: "prop", st: "state",
};
/** Delegates to the schema package so there is one definition of a valid id. */
export function newId(prefix, taken) {
    const id = generateV4Id(KIND_OF[prefix], taken);
    taken.add(id);
    step("newId", `${id} (collision-checked against ${taken.size - 1} live ids)`);
    return id;
}
/**
 * Finish a mutation by rebuilding the derived indexes from the current raw
 * document. Bound documents also write through; unbound review sessions stay
 * in memory until their explicit checkpoint commit.
 */
export function save(doc) {
    Object.assign(doc, parse(doc.raw, doc.path));
    if (!doc.path) {
        step("save", "skipped: document is not bound to a file");
        return;
    }
    commit(doc, doc.path);
}
/** Write the document to `path` and bind it there. */
/**
 * Write the document out. Binding is `load`/`parse`'s job, so committing an
 * unbound document does not bind it: a caller that commits a checkpoint mid-run
 * must stay unbound, or every later mutation's own `save` would start writing
 * the whole document to disk again.
 */
export function commit(doc, path = doc.path) {
    if (!path)
        throw new Error("commit needs a path: this document is not bound to a file");
    const tmp = `${path}.tmp-${process.pid}`;
    fs.writeFileSync(tmp, JSON.stringify(doc.raw, null, 2) + "\n");
    fs.renameSync(tmp, path);
    step("commit", `${fs.statSync(path).size} bytes -> ${path} (tmp + atomic rename)`);
}
